// ============================================================
// CarteProposition — ce que l'assistant propose, À VALIDER.
// ------------------------------------------------------------
// La carte montre tout ce qui sera écrit : la phrase EXACTE (la source de
// vérité), la reformulation, les agents, la portée, la nature, et ce que la
// règle aurait concerné. Rien n'agit avant « Valider » — un clic distinct,
// réservé à Charles et Philippe, même quand c'est eux qui ont proposé.
// ============================================================

import { useState } from 'react';
import { Link } from 'react-router';
import Badge from '../../ui/Badge.jsx';
import Button from '../../ui/Button.jsx';
import api from '../../lib/api.js';
import { useSession } from '../../lib/session.jsx';
import { formatEuro, formatPct } from '../../lib/format.js';
import { BancLecture, bancPasse } from './BancLecture.jsx';

const STATUTS = {
  pending: { ton: 'warn', libelle: 'En attente de validation' },
  validee: { ton: 'ok', libelle: 'Validée — en vigueur pour les prochaines estimations' },
  refusee: { ton: 'neutral', libelle: 'Refusée' },
  annulee: { ton: 'neutral', libelle: 'Annulée' },
};

export function CarteProposition({ proposition: p, onChange, onModifier }) {
  const { peutValiderRegles } = useSession();
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const apres = p.apres ?? {};
  const statut = STATUTS[p.statut] ?? STATUTS.pending;
  const sim = p.simulation;
  // Une règle de LECTURE (étape 4) ne se simule pas sur des pièces : elle
  // se mesure, et « Valider » attend son banc.
  const lecture = Boolean(sim?.banc_requis);

  async function geste(chemin, corps = {}) {
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await api.post(chemin, corps);
      onChange?.(r.proposition ?? null);
    } catch (err) {
      setErreur(err.message);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="assistant-carte">
      <div className="row row--between">
        <p className="caps">{`Proposition n° ${p.id}`}</p>
        <Badge tone={statut.ton}>{statut.libelle}</Badge>
      </div>
      <p><strong>{p.resume}</strong></p>
      {apres.phrase_exacte ? <p>Phrase exacte : <q>{apres.phrase_exacte}</q></p> : null}
      {apres.reformulation ? <p>Reformulation : {apres.reformulation}</p> : null}
      {p.avant?.phrase_exacte && p.type === 'regle_modification' ? (
        <p className="field__hint">Remplace : <q>{p.avant.phrase_exacte}</q></p>
      ) : null}
      {apres.agents ? (
        <p className="field__hint">
          {`Agents : ${apres.agents.join(', ')} · portée : ${apres.portee} · ${apres.nature === 'effet' ? 'effet chiffré' : 'texte'}`}
        </p>
      ) : null}
      {apres.effet_texte ? (
        <p>Effet, appliqué par le calcul : <strong>{apres.effet_texte}</strong> — jamais sur une ligne corrigée à la main.</p>
      ) : null}
      {sim?.devis?.length ? (
        <div className="field__hint">
          <p>Rejoué sur les derniers devis concernés (prix d’usinage, rien n’est modifié) :</p>
          <ul>
            {sim.devis.map((d) => (
              <li key={d.devis}>
                <Link to={`/aller/devis/${encodeURIComponent(d.devis)}`}>{d.devis}</Link>
                {` — ${formatEuro(d.usinage_avant)} → ${formatEuro(d.usinage_apres)} (${d.ecart_eur >= 0 ? '+' : ''}${formatEuro(d.ecart_eur)}`}
                {d.ecart_pct != null ? `, ${d.ecart_pct >= 0 ? '+' : ''}${formatPct(d.ecart_pct)})` : ')'}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {lecture ? (
        <BancLecture proposition={p} peutLancer={peutValiderRegles} envoi={envoi} onGeste={geste} onRelire={onChange} />
      ) : null}
      {sim && !lecture ? (
        <div className="field__hint">
          <p>{`Elle aurait concerné ${sim.pieces_concernees} pièce(s) sur les ${sim.pieces_examinees} plus récentes. Aucun devis existant n’est recalculé.`}</p>
          {sim.exemples?.length ? (
            <ul>
              {sim.exemples.map((e) => (
                <li key={e.piece_id}>
                  <Link to={`/aller/piece/${e.piece_id}`}>{e.ref ?? e.nom ?? `pièce n° ${e.piece_id}`}</Link>
                  {` — ${e.devis}${e.client ? `, ${e.client}` : ''}${e.matiere ? `, ${e.matiere}` : ''}`}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {p.valide_par_nom ? (
        <p className="field__hint">{`${p.statut === 'refusee' ? 'Refusée' : 'Validée'} par ${p.valide_par_nom}`}</p>
      ) : null}
      {erreur ? <p className="field__error" role="alert">{erreur}</p> : null}

      <div className="decoupe__actions">
        {p.statut === 'pending' && peutValiderRegles ? (
          <>
            <Button size="sm" variant="primary" disabled={envoi || (lecture && !bancPasse(p))}
              onClick={() => geste(`/assistant/propositions/${p.id}/valider`)}>Valider</Button>
            <Button size="sm" variant="ghost" disabled={envoi} onClick={() => geste(`/assistant/propositions/${p.id}/refuser`)}>Refuser</Button>
          </>
        ) : null}
        {p.statut === 'pending' ? (
          <Button size="sm" variant="ghost" disabled={envoi} onClick={() => onModifier?.(p)}>Modifier dans le chat</Button>
        ) : null}
        {p.statut === 'pending' && !peutValiderRegles ? (
          <span className="field__hint">Charles ou Philippe la valideront.</span>
        ) : null}
        {p.statut === 'validee' && peutValiderRegles && p.type !== 'regle_desactivation' && p.cible_id ? (
          <Button size="sm" variant="secondary" disabled={envoi}
            onClick={() => geste(`/regles/${p.cible_id}/activation`, { active: false })}>
            Désactiver la règle
          </Button>
        ) : null}
        {p.statut === 'validee' && peutValiderRegles ? (
          <Button size="sm" variant="ghost" disabled={envoi} onClick={() => geste(`/assistant/propositions/${p.id}/annuler`)}>Annuler</Button>
        ) : null}
      </div>
    </div>
  );
}

export default CarteProposition;
